"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.isScheduleActive = void 0;
var luxon_1 = require("luxon");
var rrule_1 = require("rrule");
var Types_1 = require("./Types");
// Adapted from https://github.com/msageryd/node-schedule-rrule/blob/master/lib/recurrenceRule.js
// to allow passing in a specific time.
function calcSafeDTstart(rule, now) {
    /*
      We want to set a new dtstart as close to current time as possible in
      order to minimise the number of past occurances, since these will degrade
      the performance of RRule.after()
      But we cannot simply set dtstart = new Date(), becase we could miss some
      details in the role.
      The concept is to back off dtstart from current time, far enough to comply
      with freq and interval. We will essentilly back off with freq*interval from current time
    */
    var _a = rule.options, count = _a.count, freq = _a.freq, dtstart = _a.dtstart, interval = _a.interval, until = _a.until;
    //If the count attribute is used we cannot safely change dtstart, since this would
    //compromise the occurance counter
    if (count)
        return null;
    //No need to do anything if we have passed the until date
    if (until && until.getTime() < now.getTime())
        return null;
    //We don't want to change dtstart if we haven't even reached that time
    if (dtstart.getTime() > now.getTime())
        return null;
    var offset;
    switch (freq) {
        case rrule_1.RRule.YEARLY:
            offset = 365 * 24 * 60 * 60 * 1000 * interval;
            break;
        case rrule_1.RRule.MONTHLY:
            offset = 31 * 24 * 60 * 60 * 1000 * interval;
            break;
        case rrule_1.RRule.WEEKLY:
            offset = 7 * 24 * 60 * 60 * 1000 * interval;
            break;
        case rrule_1.RRule.DAILY:
            offset = 24 * 60 * 60 * 1000 * interval;
            break;
        case rrule_1.RRule.HOURLY:
            offset = 60 * 60 * 1000 * interval;
            break;
        case rrule_1.RRule.MINUTELY:
            offset = 60 * 1000 * interval;
            break;
        case rrule_1.RRule.SECONDLY:
            offset = 1000 * interval;
            break;
        default:
            throw new Error("RRule has invalid freq: ".concat(freq));
    }
    return new Date(now.getTime() - offset);
}
function occurrenceIsActive(now, occurrenceJSDate, durationMs, tzStr) {
    // The occurrenceJSDate comes from rrule, which is a JS date object in UTC time.
    // Since we don't pass tzid to rrule, it will have the wall clock time
    // of the schedule's timezone.
    // Convert it to a Luxon time so it can be compared with the current time.
    var occurrence = luxon_1.DateTime.fromISO(occurrenceJSDate.toISOString(), {
        // Since the ISOString will contain a "Z" indicating UTC time,
        // this sets the Luxon date to UTC instead of shifting to the local timezone,
        // which would alter the wall clock time unnecessarily.
        setZone: true
    });
    // Then set the schedule's timezone on the Luxon date so it's in the correct timezone, not UTC.
    occurrence = occurrence.setZone(tzStr, {
        keepLocalTime: true
    });
    // Do the actual schedule comparison to determine if it's active.
    var end = occurrence.plus(durationMs);
    console.debug('testing occurrence:', occurrence.toISO(), now.toISO(), end.toISO());
    var isActive = occurrence <= now && now < end;
    return isActive;
}
var isScheduleActive = function (schedule, isMultitimezoneEnabled) {
    var scheduleRRule = schedule.recurrenceRule;
    // Calculate the schedule duration.
    var startTime = Date.parse(schedule.startDatetime);
    var endTime = Date.parse(schedule.endDatetime);
    var durationMs = endTime - startTime;
    if (isNaN(durationMs) || durationMs <= 0) {
        return false;
    }
    console.debug('Playlist TZID: ', schedule.tzid);
    // Replacing playlist timezone with local device timezone if multi timezone is enabled
    if (isMultitimezoneEnabled) {
        schedule.tzid = Intl.DateTimeFormat().resolvedOptions().timeZone;
    }
    console.debug('Final TZID ', schedule.tzid, ' with Multitimezone ', isMultitimezoneEnabled);
    // 1. Get the Luxon local time (has local timezone). This will be used for evaluating
    //    whether current time is "inside" any occurrences found.
    var now = luxon_1.DateTime.local();
    // 2. Get the local time as a JS date object in the schedule's timezone, with a
    //    UTC timezone. This will be passed into rrule to evaluate occurrences,
    //    because the rrule object operates as if the schedule's timezone is UTC (since
    //    we don't pass tzid into rrule due to DST-related bugs in the rrule package).
    var nowJS = luxon_1.DateTime.local()
        .setZone(schedule.tzid)
        .setZone('UTC', { keepLocalTime: true })
        .toJSDate();
    console.debug('>>>>>>>>>> starting schedule eval <<<<<<<<<<<<');
    console.debug('now:', now.toISO());
    console.debug('schedule start time:', schedule.startDatetime, schedule.tzid);
    // 3. Find the first occurrence time. Do this by creating a single-occurrence rrule.
    var firstOccurrenceRRule = new rrule_1.RRule({
        freq: rrule_1.RRule.YEARLY,
        dtstart: new Date(Date.parse(schedule.startDatetime)),
        interval: 1,
        count: 1
        // WARNING: DO NOT USE TZID FOR RRULES
        //          RRule tzid implementation has DST-related bugs and should not be used.
    });
    console.debug('=========== first occurrence ===========');
    console.debug(firstOccurrenceRRule.all(function (date, i) {
        return date && i < 2;
    }));
    var firstOccurrence = firstOccurrenceRRule.before(nowJS, true);
    if (!firstOccurrence) {
        return false;
    }
    // If first occurrence is active, return true.
    if (occurrenceIsActive(now, firstOccurrence, durationMs, schedule.tzid)) {
        console.debug('first occurrence ACTIVE:', firstOccurrence.toISOString(), schedule.tzid);
        return true;
    }
    console.debug('first occurrence NOT ACTIVE:', firstOccurrence.toISOString(), schedule.tzid);
    // If first occurrence is not active and there are no recurrences, return false.
    if (!scheduleRRule) {
        return false;
    }
    // 4. If the schedule has a recurrence rule, generate the last valid recurrence
    //    and check whether it's active.
    // Hack to make sure dtstart string are in UTC format, since the API
    // may return dates formatted like '2020-11-03T19:00:00' in the rrule.
    // Better way would be to extract date/time values and use Date.UTC().
    if (scheduleRRule.dtstart.slice(-1) !== 'Z') {
        scheduleRRule.dtstart = scheduleRRule.dtstart.concat('Z');
    }
    var rrule = new rrule_1.RRule({
        freq: Types_1.frequencyMap[scheduleRRule.freq],
        // Default to interval of 1 if not passed, because missing interval causes hang calculating recurrence.
        interval: scheduleRRule.interval ? scheduleRRule.interval : 1,
        byweekday: scheduleRRule.byday
            ? scheduleRRule.byday.map(function (elem) {
                return rrule_1.Weekday.fromStr(elem);
            })
            : undefined,
        dtstart: new Date(Date.parse(scheduleRRule.dtstart))
        // WARNING: DO NOT USE TZID FOR RRULES
        //          RRule tzid implementation has DST-related bugs and should not be used.
    });
    // Adjust the rrule's dtstart for performance reasons.
    // Resets the rrule's dtstart to one interval in the past,
    // to avoid generating too many recurrences.
    var safeDtstart = calcSafeDTstart(rrule, nowJS);
    if (safeDtstart) {
        rrule.options.dtstart = safeDtstart;
        console.debug('safe dtstart found, updated from', scheduleRRule.dtstart, '->', safeDtstart);
    }
    console.debug('rrule summary:', rrule.toText());
    // Get the last occurrence before or at the current time.
    console.debug('=========== recurrences ===========');
    console.debug(rrule.all(function (date, i) {
        return date && i < 2;
    }));
    var occurrenceTime = rrule.before(nowJS, true);
    console.debug(occurrenceTime);
    // If no such occurrences, schedule is not active.
    if (!occurrenceTime) {
        console.debug('no recurrences found');
        return false;
    }
    // Otherwise, check if the occurrence is currently active.
    if (occurrenceIsActive(now, occurrenceTime, durationMs, schedule.tzid)) {
        console.debug('recurrence ACTIVE:', occurrenceTime.toISOString(), schedule.tzid);
        return true;
    }
    console.debug('recurrence NOT ACTIVE:', occurrenceTime.toISOString(), schedule.tzid);
    return false;
};
exports.isScheduleActive = isScheduleActive;
